import { useParams, Link } from "react-router-dom";
import {
  ArrowLeft,
  KeyRound,
  Shield,
  Check,
  X,
  Eye,
} from "lucide-react";
import { useAppRole } from "@/hooks/use-app-roles";
import type { OrgUnitType } from "@/types/org";
import { RoleBadge } from "@/components/shared/RoleBadge";
import { OrgUnitTypeBadge } from "@/components/shared/OrgUnitTypeBadge";
import { Badge, Button, Card, CardContent, CardDescription, CardHeader, CardTitle, ScrollArea, Separator, Skeleton, Table, TableBody, TableCell, TableHead, TableHeader, TableRow, Tabs, TabsContent, TabsList, TabsTrigger } from "@fox1ck-org/ui";

function formatAge(dateStr: string): string {
  const now = Date.now();
  const created = new Date(dateStr).getTime();
  const diffMs = now - created;
  const diffMin = Math.floor(diffMs / 60_000);
  if (diffMin < 1) return "только что";
  if (diffMin < 60) return `${diffMin} мин назад`;
  const diffHrs = Math.floor(diffMin / 60);
  if (diffHrs < 24) return `${diffHrs} ч назад`;
  const diffDays = Math.floor(diffHrs / 24);
  if (diffDays < 30) return `${diffDays} дн назад`;
  const diffMonths = Math.floor(diffDays / 30);
  return `${diffMonths} мес назад`;
}

export default function AppRoleDetail() {
  const { name } = useParams<{ name: string }>();
  const { data: binding, isLoading } = useAppRole(name!);

  if (isLoading || !binding) {
    return (
      <div className="space-y-6">
        <div className="flex items-center gap-4">
          <Skeleton className="h-5 w-5" />
          <div className="space-y-2">
            <Skeleton className="h-7 w-48" />
            <Skeleton className="h-4 w-24" />
          </div>
        </div>
        <Skeleton className="h-10 w-64" />
        <Skeleton className="h-64 w-full rounded-lg" />
      </div>
    );
  }

  const mapperCount = binding.protocolMappers?.length ?? 0;

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-4">
          <Button variant="ghost" size="icon" asChild>
            <Link to="/app-roles">
              <ArrowLeft className="h-5 w-5" />
            </Link>
          </Button>
          <div className="flex items-center gap-3">
            <div className="flex items-center justify-center w-10 h-10 rounded-lg bg-primary/10">
              <KeyRound className="h-5 w-5 text-primary" />
            </div>
            <div>
              <h1 className="text-[17px] font-semibold text-foreground">{binding.clientId}</h1>
              <p className="text-sm text-muted-foreground">{binding.namespace}/{binding.name}</p>
            </div>
          </div>
        </div>
      </div>

      {/* Status bar */}
      <div className="flex items-center gap-4 flex-wrap">
        {binding.status.ready ? (
          <Badge variant="ok">
            <Check className="h-3 w-3 mr-1" />
            Готово
          </Badge>
        ) : (
          <Badge variant="danger">
            <X className="h-3 w-3 mr-1" />
            Не готово
          </Badge>
        )}
        <Separator orientation="vertical" className="h-5" />
        <span className="text-sm text-muted-foreground">
          {binding.roles.length} role{binding.roles.length !== 1 ? "s" : ""}
        </span>
        <Separator orientation="vertical" className="h-5" />
        <span className="text-sm text-muted-foreground">
          {mapperCount} mapper{mapperCount !== 1 ? "s" : ""}
        </span>
        <Separator orientation="vertical" className="h-5" />
        <span className="text-sm text-muted-foreground">
          Создан {formatAge(binding.createdAt)}
        </span>
        {binding.orgUnitTypes.length > 0 && (
          <>
            <Separator orientation="vertical" className="h-5" />
            <div className="flex items-center gap-1">
              {binding.orgUnitTypes.map((t) => (
                <OrgUnitTypeBadge key={t} type={t as OrgUnitType} />
              ))}
            </div>
          </>
        )}
      </div>

      {/* Tabs */}
      <Tabs defaultValue="roles">
        <TabsList>
          <TabsTrigger value="roles">
            <Shield className="h-3.5 w-3.5 mr-1.5" />
            Роли
          </TabsTrigger>
          <TabsTrigger value="mappers">
            <Eye className="h-3.5 w-3.5 mr-1.5" />
            Protocol Mappers
          </TabsTrigger>
          <TabsTrigger value="yaml">
            YAML
          </TabsTrigger>
        </TabsList>

        {/* Roles Tab */}
        <TabsContent value="roles" className="space-y-4 mt-4">
          {binding.roles.length === 0 ? (
            <Card>
              <CardContent className="py-8 text-center text-muted-foreground">
                В привязке нет ролей.
              </CardContent>
            </Card>
          ) : (
            <div className="rounded-md border">
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>Название роли</TableHead>
                    <TableHead>Описание</TableHead>
                    <TableHead>Составная</TableHead>
                    <TableHead>Вложенные роли</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {binding.roles.map((role) => (
                    <TableRow key={role.name}>
                      <TableCell>
                        <RoleBadge name={role.name} />
                      </TableCell>
                      <TableCell className="text-muted-foreground">
                        {role.description || "-"}
                      </TableCell>
                      <TableCell>
                        {role.composite ? (
                          <Check className="h-4 w-4 text-green-400" />
                        ) : (
                          <span className="text-muted-foreground">-</span>
                        )}
                      </TableCell>
                      <TableCell>
                        {role.compositeChildren && role.compositeChildren.length > 0 ? (
                          <div className="flex flex-wrap gap-1">
                            {role.compositeChildren.map((child) => (
                              <RoleBadge key={child} name={child} />
                            ))}
                          </div>
                        ) : (
                          <span className="text-muted-foreground">-</span>
                        )}
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </div>
          )}

          {/* Provisioned status */}
          {binding.status.provisionedRoles.length > 0 && (
            <Card>
              <CardHeader>
                <CardTitle className="text-sm">Созданные роли</CardTitle>
                <CardDescription>
                  Роли, которые сейчас есть в Keycloak у клиента{" "}
                  <span className="font-mono text-foreground">{binding.clientId}</span>
                </CardDescription>
              </CardHeader>
              <CardContent>
                <div className="flex flex-wrap gap-1.5">
                  {binding.status.provisionedRoles.map((r) => (
                    <Badge key={r} variant="outline" className="font-mono text-xs">
                      <Check className="h-3 w-3 mr-1 text-green-400" />
                      {r}
                    </Badge>
                  ))}
                </div>
              </CardContent>
            </Card>
          )}
        </TabsContent>

        {/* Protocol Mappers Tab */}
        <TabsContent value="mappers" className="space-y-4 mt-4">
          {mapperCount === 0 ? (
            <Card>
              <CardContent className="py-8 text-center text-muted-foreground">
                В привязке нет protocol mappers.
              </CardContent>
            </Card>
          ) : (
            <div className="grid gap-4 sm:grid-cols-2">
              {binding.protocolMappers.map((mapper) => (
                <Card key={mapper.name}>
                  <CardHeader>
                    <CardTitle className="text-sm font-mono">{mapper.name}</CardTitle>
                    <CardDescription>{mapper.mapperType}</CardDescription>
                  </CardHeader>
                  <CardContent>
                    <div className="space-y-3 text-sm">
                      <div className="flex justify-between">
                        <span className="text-muted-foreground">Название claim</span>
                        <span className="font-mono text-foreground">{mapper.claimName}</span>
                      </div>
                      <Separator />
                      <div className="flex justify-between">
                        <span className="text-muted-foreground">Множественный</span>
                        <span className="text-foreground">{mapper.multivalued ? "Да" : "Нет"}</span>
                      </div>
                      <Separator />
                      <div className="flex justify-between">
                        <span className="text-muted-foreground">ID-токен</span>
                        {mapper.addToIdToken ? (
                          <Check className="h-4 w-4 text-green-400" />
                        ) : (
                          <X className="h-4 w-4 text-muted-foreground" />
                        )}
                      </div>
                      <Separator />
                      <div className="flex justify-between">
                        <span className="text-muted-foreground">Токен доступа</span>
                        {mapper.addToAccessToken ? (
                          <Check className="h-4 w-4 text-green-400" />
                        ) : (
                          <X className="h-4 w-4 text-muted-foreground" />
                        )}
                      </div>
                    </div>
                  </CardContent>
                </Card>
              ))}
            </div>
          )}

          {/* Provisioned mappers */}
          {binding.status.provisionedMappers.length > 0 && (
            <Card>
              <CardHeader>
                <CardTitle className="text-sm">Созданные mappers</CardTitle>
                <CardDescription>
                  Mappers, которые сейчас есть в Keycloak
                </CardDescription>
              </CardHeader>
              <CardContent>
                <div className="flex flex-wrap gap-1.5">
                  {binding.status.provisionedMappers.map((m) => (
                    <Badge key={m} variant="outline" className="font-mono text-xs">
                      <Check className="h-3 w-3 mr-1 text-green-400" />
                      {m}
                    </Badge>
                  ))}
                </div>
              </CardContent>
            </Card>
          )}
        </TabsContent>

        {/* YAML Tab */}
        <TabsContent value="yaml" className="mt-4">
          <Card>
            <CardHeader>
              <CardTitle className="text-sm">Спецификация и статус CRD</CardTitle>
              <CardDescription>
                Исходные данные AppRoleBinding
              </CardDescription>
            </CardHeader>
            <CardContent>
              <ScrollArea className="h-[500px] w-full">
                <pre className="text-xs font-mono text-foreground bg-muted/50 rounded-lg p-4 overflow-x-auto">
                  {JSON.stringify(
                    {
                      metadata: {
                        name: binding.name,
                        namespace: binding.namespace,
                        createdAt: binding.createdAt,
                      },
                      spec: {
                        clientId: binding.clientId,
                        orgUnitTypes: binding.orgUnitTypes,
                        roles: binding.roles,
                        protocolMappers: binding.protocolMappers,
                        configMapRef: binding.configMapRef,
                      },
                      status: binding.status,
                    },
                    null,
                    2
                  )}
                </pre>
              </ScrollArea>
            </CardContent>
          </Card>
        </TabsContent>
      </Tabs>
    </div>
  );
}
